import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Agent } from '@localagent/sdk';
import { Badge } from '@localagent/ui/components/badge';
import { DataList } from '@localagent/ui/components/data-list';
import { ListAddButton } from '@localagent/ui/components/list-filter-menu';
import { useFilteredList } from '@localagent/ui/hooks/use-filtered-list';
import { usePagination } from '@localagent/ui/hooks/use-pagination';
import { useAgents } from '../../hooks/useAgents';
import { CreateAgentDialog } from './AgentDialogs';

const searchFn = (agent: Agent, query: string): boolean => {
  const q = query.toLowerCase();
  return (
    agent.name.toLowerCase().includes(q) ||
    agent.persona_name.toLowerCase().includes(q) ||
    (agent.description?.toLowerCase().includes(q) ?? false)
  );
};

export function AgentRoster() {
  const navigate = useNavigate();
  const agents = useAgents();
  const [createOpen, setCreateOpen] = useState(false);

  const sorted = useMemo(
    () => [...(agents.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [agents.data],
  );

  const { filteredItems, searchValue, onSearchChange } = useFilteredList({ items: sorted, searchFn });

  const pagination = usePagination({
    totalItems: filteredItems.length,
    pageSize: 25,
    pageSizeOptions: [10, 25, 50, 100],
  });

  useEffect(() => {
    pagination.reset();
  }, [searchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const pagedItems = filteredItems.slice(pagination.startIndex, pagination.endIndex);

  const openAgent = (agentId: string) => {
    void navigate(`/agents/${agentId}`);
  };

  const renderItem = (agent: Agent) => (
    <button
      type="button"
      data-testid={`agent-row-${agent.id}`}
      data-persona-id={agent.persona_id}
      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-accent/50"
      onClick={() => openAgent(agent.id)}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{agent.name}</span>
          {agent.is_default && (
            <Badge data-testid={`agent-row-default-${agent.id}`} variant="secondary">Default</Badge>
          )}
        </div>
        <p className="mt-0.5 truncate text-body text-muted-foreground">
          Persona: <span data-testid={`agent-row-persona-${agent.id}`}>{agent.persona_name}</span>
          {agent.description ? ` · ${agent.description}` : ''}
        </p>
      </div>
    </button>
  );

  return (
    <div data-testid="agent-roster-view">
      <DataList<Agent>
        items={pagedItems}
        renderItem={renderItem}
        getItemId={(agent) => agent.id}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder="Search agents..."
        page={pagination.page}
        totalPages={pagination.totalPages}
        onPageChange={pagination.onPageChange}
        totalItems={filteredItems.length}
        pageSize={pagination.pageSize}
        pageSizeOptions={pagination.pageSizeOptions}
        onPageSizeChange={pagination.onPageSizeChange}
        isLoading={agents.isLoading}
        headerActions={
          <ListAddButton label="New agent" testId="agent-roster-new-button" onClick={() => setCreateOpen(true)} />
        }
        emptyState={
          <div className="text-center" data-testid="agent-roster-empty">
            <p className="text-body text-muted-foreground">
              {agents.error
                ? `Failed to load agents: ${agents.error.message}`
                : searchValue
                  ? 'No agents match your search.'
                  : 'No agents yet. Create a persona, or add an Agent from one.'}
            </p>
          </div>
        }
      />
      <CreateAgentDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => setCreateOpen(false)}
      />
    </div>
  );
}
